import { useState } from "react";
import { apiAdmin, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Minus, Plus, Save, Clock } from "lucide-react";
import { WallGrid, TvTray } from "@/components/videowall/WallGrid";
import WallContent from "@/components/videowall/WallContent";
import WallSyncPanel from "@/components/videowall/WallSyncPanel";
import { toast } from "sonner";

const TEMPLATES = [
  { id: "h2", label: "Horizontal 2", cols: 2, rows: 1 },
  { id: "h3", label: "Horizontal 3", cols: 3, rows: 1 },
  { id: "h4", label: "Horizontal 4", cols: 4, rows: 1 },
  { id: "v2", label: "Vertical 2", cols: 1, rows: 2 },
  { id: "v3", label: "Vertical 3", cols: 1, rows: 3 },
  { id: "q4", label: "Cuadrado 2×2", cols: 2, rows: 2 },
  { id: "q6", label: "3×2", cols: 3, rows: 2 },
];
const INTERVALS = [5, 10, 15, 20, 30, 60];
const DURATIONS = [[30, "30 seg"], [60, "1 min"], [120, "2 min"], [180, "3 min"], [300, "5 min"]];
const ITEM_SECONDS = [5, 8, 10, 15, 20, 30];
const REPEATS = [1, 2, 3, 4, 6, 12];
const MODES = [["playlist", "Dentro de la lista"], ["interval", "Cada X minutos"]];

function TemplateIcon({ cols, rows }) {
  return (
    <div className="grid gap-[2px]" style={{ gridTemplateColumns: `repeat(${cols}, 10px)`, gridTemplateRows: `repeat(${rows}, 6px)` }}>
      {Array.from({ length: cols * rows }).map((_, i) => <div key={i} className="bg-current rounded-[1px]" />)}
    </div>
  );
}

function Stepper({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-500 w-16">{label}</span>
      <Button type="button" size="icon" variant="outline" className="h-7 w-7" onClick={() => onChange(Math.max(1, value - 1))} data-testid={`${testid}-minus`}><Minus className="h-3 w-3" /></Button>
      <span className="w-5 text-center font-mono text-sm" data-testid={`${testid}-value`}>{value}</span>
      <Button type="button" size="icon" variant="outline" className="h-7 w-7" onClick={() => onChange(Math.min(4, value + 1))} data-testid={`${testid}-plus`}><Plus className="h-3 w-3" /></Button>
    </div>
  );
}

function nextRunLabel(wall) {
  const w = wall.window;
  if (!w || !wall.enabled) return "Apagado";
  if (wall.mode === "playlist") return "Dentro de la lista de cada TV";
  const t = new Date(w.starts_at).toLocaleTimeString("es-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" });
  return w.active ? "Mostrándose ahora" : `Próxima vez: ${t}`;
}

export default function WallEditor({ wall, tvs, media, onChanged }) {
  const [name, setName] = useState(wall.name);
  const [cols, setCols] = useState(wall.cols);
  const [rows, setRows] = useState(wall.rows);
  const [slots, setSlots] = useState(wall.slots);
  const [interval, setIntervalMin] = useState(wall.interval_minutes);
  const [duration, setDuration] = useState(wall.duration_seconds);
  const [enabled, setEnabled] = useState(wall.enabled);
  const [mode, setMode] = useState(wall.mode || "interval");
  const [itemSeconds, setItemSeconds] = useState(wall.item_seconds || 10);
  const [wallRepeat, setWallRepeat] = useState(wall.wall_repeat || 1);
  const [saveCount, setSaveCount] = useState(0);
  const [selectedTv, setSelectedTv] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const touch = (fn) => (...a) => { fn(...a); setDirty(true); };
  const tvsById = Object.fromEntries(tvs.map((t) => [t.id, t]));
  const placed = new Set(slots.map((s) => s.tv_id));
  const visibleSlots = slots.filter((s) => s.col < cols && s.row < rows && tvsById[s.tv_id]);

  const resize = touch((c, r) => {
    setCols(c); setRows(r);
    setSlots((list) => list.filter((s) => s.col < c && s.row < r));
  });

  const place = touch((tvId, c, r) => {
    if (!tvId) return;
    setSlots((list) => {
      const from = list.find((s) => s.tv_id === tvId);
      const occupant = list.find((s) => s.col === c && s.row === r);
      let next = list.filter((s) => s.tv_id !== tvId && s !== occupant);
      if (occupant && occupant.tv_id !== tvId && from) next.push({ ...occupant, col: from.col, row: from.row });
      next.push({ tv_id: tvId, col: c, row: r });
      return next;
    });
    setSelectedTv(null);
  });
  const unplace = touch((tvId) => setSlots((list) => list.filter((s) => s.tv_id !== tvId)));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await apiAdmin.patch(`/video-walls/${wall.id}`, {
        name: name.trim() || wall.name, cols, rows, slots: visibleSlots,
        interval_minutes: interval, duration_seconds: duration, enabled,
        mode, item_seconds: itemSeconds, wall_repeat: wallRepeat,
      });
      setDirty(false);
      setSaveCount((n) => n + 1);
      onChanged(data);
      toast.success("Video Wall guardado");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const ready = wall.items.filter((i) => i.status !== "failed");
  const preview = wall.items.find((i) => i.media_id === previewId) || ready[0];

  return (
    <div className="space-y-6" data-testid="wall-editor">
      <div className="flex flex-wrap items-end gap-4 justify-between">
        <div className="min-w-[240px] flex-1 max-w-md">
          <Label className="text-xs">Nombre del grupo</Label>
          <Input value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }} className="mt-1" data-testid="wall-name-input" />
        </div>
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1.5 text-xs ${wall.window?.active ? "text-emerald-600" : "text-slate-500"}`} data-testid="wall-next-run">
            <Clock className="h-3.5 w-3.5" />{nextRunLabel(wall)}
          </span>
          <Button onClick={save} disabled={saving || !dirty} data-testid="wall-save-btn">
            <Save className="h-4 w-4 mr-1.5" />{saving ? "Guardando…" : dirty ? "Guardar cambios" : "Guardado"}
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-5">
        <div className="flex flex-wrap gap-2" data-testid="wall-templates">
          {TEMPLATES.map((t) => {
            const on = t.cols === cols && t.rows === rows;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => resize(t.cols, t.rows)}
                className={`flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-medium transition-colors duration-200 ${
                  on ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-700 hover:border-slate-400"
                }`}
                data-testid={`wall-template-${t.id}`}
              >
                <TemplateIcon cols={t.cols} rows={t.rows} />{t.label}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-6">
          <Stepper label="Columnas" value={cols} onChange={(v) => resize(v, rows)} testid="wall-cols" />
          <Stepper label="Filas" value={rows} onChange={(v) => resize(cols, v)} testid="wall-rows" />
        </div>
        <WallGrid
          cols={cols} rows={rows} slots={visibleSlots} tvsById={tvsById} preview={preview}
          selectedTv={selectedTv} onDropTv={place} onPlace={place} onRemove={unplace} onDragTv={() => {}}
        />
        <TvTray tvs={tvs.filter((t) => !placed.has(t.id))} selectedTv={selectedTv} onSelect={setSelectedTv} onDropBack={unplace} />
        {(cols !== wall.cols || rows !== wall.rows) && wall.items.length > 0 && (
          <p className="text-xs text-amber-600">Al guardar, el contenido se volverá a cortar para la nueva cuadrícula.</p>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <WallContent wall={wall} media={media} onChanged={onChanged} previewId={preview?.media_id} onPreview={setPreviewId} mode={mode} />
        <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-5" data-testid="wall-settings">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-heading text-base font-semibold text-slate-900">¿Cómo aparece el Wall?</div>
              <p className="text-xs text-slate-500">{mode === "playlist" ? "Sale al final del menú de cada TV y vuelve a empezar." : "Interrumpe el menú cada cierto tiempo."}</p>
            </div>
            <Switch checked={enabled} onCheckedChange={(v) => { setEnabled(v); setDirty(true); }} data-testid="wall-enabled-switch" />
          </div>
          <div className="inline-flex rounded-full bg-slate-100 p-1" data-testid="wall-mode-toggle">
            {MODES.map(([m, l]) => (
              <button
                key={m}
                type="button"
                onClick={() => { setMode(m); setDirty(true); }}
                className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors duration-200 ${mode === m ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                data-testid={`wall-mode-${m}`}
              >{l}</button>
            ))}
          </div>
          {mode === "playlist" ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Segundos por foto (todas las TVs)</Label>
                  <Select value={String(itemSeconds)} onValueChange={(v) => { setItemSeconds(Number(v)); setDirty(true); }}>
                    <SelectTrigger className="mt-1" data-testid="wall-item-seconds-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(ITEM_SECONDS.includes(itemSeconds) ? ITEM_SECONDS : [itemSeconds, ...ITEM_SECONDS]).map((x) => (
                        <SelectItem key={x} value={String(x)} data-testid={`wall-item-seconds-${x}`}>{x} seg</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">El Wall dura{wall.items.length > 1 ? " (por contenido)" : ""}</Label>
                  <Select value={String(wallRepeat)} onValueChange={(v) => { setWallRepeat(Number(v)); setDirty(true); }}>
                    <SelectTrigger className="mt-1" data-testid="wall-repeat-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(REPEATS.includes(wallRepeat) ? REPEATS : [wallRepeat, ...REPEATS]).map((x) => (
                        <SelectItem key={x} value={String(x)} data-testid={`wall-repeat-${x}`}>{x * itemSeconds} seg</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <WallSyncPanel wallId={wall.id} version={`${saveCount}-${wall.items.length}`} disabled={dirty} />
            </>
          ) : (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Cada</Label>
              <Select value={String(interval)} onValueChange={(v) => {
                const m = Number(v);
                setIntervalMin(m);
                if (duration >= m * 60) setDuration(Math.max(30, m * 60 - 60));
                setDirty(true);
              }}>
                <SelectTrigger className="mt-1" data-testid="wall-interval-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(INTERVALS.includes(interval) ? INTERVALS : [interval, ...INTERVALS]).map((m) => <SelectItem key={m} value={String(m)} data-testid={`wall-interval-${m}`}>{m} minutos</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Dura</Label>
              <Select value={String(duration)} onValueChange={(v) => { setDuration(Number(v)); setDirty(true); }}>
                <SelectTrigger className="mt-1" data-testid="wall-duration-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DURATIONS.map(([s, l]) => (
                    <SelectItem key={s} value={String(s)} disabled={s >= interval * 60} data-testid={`wall-duration-${s}`}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          )}
          {mode === "interval" && (
            <p className="text-[11px] leading-relaxed text-slate-500 bg-slate-50 rounded-lg p-3">
              Con la app actual del Fire Stick cada TV se une a la imagen gigante dentro de unos 30 segundos.
              Por eso recomendamos 1 minuto o más.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
